import Papa from 'papaparse';
import { headerKey } from '../../../import/csv-import';

/**
 * The pure half of the key/value settings editor: checking pairs, reading a CSV into pairs and
 * merging them into what the setting already holds. The setting's value is a JSON array of these
 * pairs (`adminSettingsDataProvider` encodes and decodes it); HQ-API checks nothing about it.
 */

export interface KeyValuePair {
    key: string;
    value: string;
}

/** Keys are compared trimmed and without case, so "Foo" and " foo" are the same key. */
export function keyOf(key: string): string {
    return key.trim().toLowerCase();
}

/** A stored pair made safe to edit: both sides strings, whatever was saved. */
export function toPair(raw: unknown): KeyValuePair {
    const record: Record<string, unknown> = (raw !== null && typeof raw === 'object') ? raw as Record<string, unknown> : {};

    return {
        key: record.key === undefined || record.key === null ? '' : String(record.key),
        value: record.value === undefined || record.value === null ? '' : String(record.value),
    };
}

/**
 * The problem with each row's key, by row index: empty, or the same as another row's. Every row
 * of a clash is flagged, not only the later ones, so either can be the one that is changed.
 */
export function keyProblems(pairs: KeyValuePair[]): Map<number, string> {
    const rowsByKey: Map<string, number[]> = new Map();
    pairs.forEach((pair: KeyValuePair, index: number): void => {
        const key: string = keyOf(pair.key);
        if (key !== '') {
            rowsByKey.set(key, [...(rowsByKey.get(key) ?? []), index]);
        }
    });

    const problems: Map<number, string> = new Map();
    pairs.forEach((pair: KeyValuePair, index: number): void => {
        if (keyOf(pair.key) === '') {
            problems.set(index, 'Key is required');
            return;
        }
        const rows: number[] = rowsByKey.get(keyOf(pair.key)) ?? [];
        if (rows.length > 1) {
            const others: string = rows
                .filter((row: number): boolean => row !== index)
                .map((row: number): string => String(row + 1))
                .join(', ');
            problems.set(index, 'Duplicate key, also on row ' + others);
        }
    });

    return problems;
}

/** One line for the editor's summary, or null when every key is fine. */
export function problemSummary(problems: Map<number, string>): string | null {
    let empty: number = 0;
    let duplicate: number = 0;
    for (const problem of problems.values()) {
        if (problem === 'Key is required') {
            empty++;
        } else {
            duplicate++;
        }
    }
    const parts: string[] = [];
    if (duplicate > 0) {
        parts.push(duplicate + ' rows share a key');
    }
    if (empty > 0) {
        parts.push(empty + (empty === 1 ? ' row has' : ' rows have') + ' no key');
    }

    return parts.length === 0 ? null : parts.join(', ') + '. Fix them to save.';
}

export interface ParsedPairs {
    pairs: KeyValuePair[];
    /** Rows with no key, which are left out. */
    skippedRows: number;
    /** Rows with more than two cells; only the first two are read. */
    extraCellRows: number;
    /** Problems with the file itself, such as an unclosed quote. */
    parseErrors: string[];
}

/**
 * Read CSV records into pairs: the first cell is the key, the second the value. A first row of
 * "key,value" (any case or spacing) is a header and is dropped; without one, every row is data.
 * Split from `parseKeyValueFile` so it can be tested without a `File`.
 */
export function pairsFromRecords(records: string[][]): Omit<ParsedPairs, 'parseErrors'> {
    let rows: string[][] = records;
    if (rows.length > 0 && headerKey(rows[0][0] ?? '') === 'key' && headerKey(rows[0][1] ?? '') === 'value') {
        rows = rows.slice(1);
    }

    const pairs: KeyValuePair[] = [];
    let skippedRows: number = 0;
    let extraCellRows: number = 0;
    for (const row of rows) {
        const key: string = (row[0] ?? '').trim();
        if (key === '') {
            skippedRows++;
            continue;
        }
        if (row.slice(2).some((cell: string): boolean => cell.trim() !== '')) {
            extraCellRows++;
        }
        pairs.push({ key: key, value: (row[1] ?? '').trim() });
    }

    return { pairs: pairs, skippedRows: skippedRows, extraCellRows: extraCellRows };
}

export function parseKeyValueFile(file: File): Promise<ParsedPairs> {
    return new Promise((resolve: (parsed: ParsedPairs) => void): void => {
        Papa.parse<string[]>(file, {
            header: false,
            skipEmptyLines: 'greedy',
            complete: (result: Papa.ParseResult<string[]>): void => {
                // Excel's byte order mark would otherwise stick to the first key.
                const records: string[][] = result.data.map((row: string[], index: number): string[] =>
                    index === 0 && row.length > 0 ? [row[0].replace(/^﻿/, ''), ...row.slice(1)] : row);
                resolve({
                    ...pairsFromRecords(records),
                    parseErrors: result.errors
                        .filter((error: Papa.ParseError): boolean => error.type !== 'FieldMismatch')
                        .map((error: Papa.ParseError): string =>
                            (error.row !== undefined ? 'Row ' + (error.row + 1) + ': ' : '') + error.message),
                });
            },
            error: (error: Error): void => {
                resolve({ pairs: [], skippedRows: 0, extraCellRows: 0, parseErrors: [error.message] });
            },
        });
    });
}

export type ImportMode = 'merge' | 'replace';

/** What one CSV row does to the setting. */
export type PairOutcome = 'new' | 'updates' | 'unchanged' | 'duplicate';

export interface PlannedPair {
    pair: KeyValuePair;
    outcome: PairOutcome;
    /** The value it replaces, for `updates`. */
    previous?: string;
}

export interface ImportPlan {
    rows: PlannedPair[];
    /** The setting's pairs once applied, in order: existing first, new rows after. */
    result: KeyValuePair[];
    /** Existing pairs a replace drops. Always 0 for a merge. */
    removed: number;
}

/**
 * What applying a CSV would do. A merge updates the value of each existing key the file names
 * (the file's spelling of the key wins) and adds the rest after. A replace makes the file the
 * whole list. Either way a key repeated within the file is kept as its own row and marked
 * `duplicate`, so nothing in the file disappears silently; the editor then flags it and Save
 * stays off until it is fixed.
 */
export function planImport(existing: KeyValuePair[], incoming: KeyValuePair[], mode: ImportMode): ImportPlan {
    const result: KeyValuePair[] = mode === 'merge' ? existing.map((pair: KeyValuePair): KeyValuePair => ({ ...pair })) : [];
    const existingIndex: Map<string, number> = new Map();
    if (mode === 'merge') {
        result.forEach((pair: KeyValuePair, index: number): void => {
            if (!existingIndex.has(keyOf(pair.key))) {
                existingIndex.set(keyOf(pair.key), index);
            }
        });
    }

    const seen: Set<string> = new Set();
    const rows: PlannedPair[] = incoming.map((pair: KeyValuePair): PlannedPair => {
        const key: string = keyOf(pair.key);
        if (seen.has(key)) {
            result.push({ ...pair });
            return { pair: pair, outcome: 'duplicate' };
        }
        seen.add(key);

        const index: number | undefined = existingIndex.get(key);
        if (index === undefined) {
            result.push({ ...pair });
            return { pair: pair, outcome: 'new' };
        }

        const previous: KeyValuePair = result[index];
        result[index] = { ...pair };
        return previous.value === pair.value && previous.key === pair.key
            ? { pair: pair, outcome: 'unchanged' }
            : { pair: pair, outcome: 'updates', previous: previous.value };
    });

    return { rows: rows, result: result, removed: mode === 'replace' ? existing.length : 0 };
}

export function pairsToCsv(pairs: KeyValuePair[]): string {
    return Papa.unparse({
        fields: ['key', 'value'],
        data: pairs.map((pair: KeyValuePair): string[] => [pair.key, pair.value]),
    }, { newline: '\r\n' });
}
